import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { ApiError, redirectToLoginAfterAuthLoss } from "@/shared/lib/apiClient";
import { meRequest } from "../api/companyApi";
import type { AuthUser } from "../api/types";
import {
  clearSession,
  getAuthToken,
  getAuthUser,
  isAuthenticated,
  storeAuth,
} from "../auth";

type AuthContextValue = {
  user: AuthUser | null;
  ready: boolean;
  refreshSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  ready: false,
  refreshSession: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => getAuthUser());
  const [ready, setReady] = useState(false);

  const refreshSession = useCallback(async () => {
    if (!getAuthToken()) {
      setUser(null);
      return;
    }

    try {
      const response = await meRequest();
      setUser(response.user);
      const token = getAuthToken();
      if (token) {
        storeAuth(token, response.user);
      }
    } catch (err) {
      clearSession();
      setUser(null);
      if (err instanceof ApiError && err.status === 401) {
        redirectToLoginAfterAuthLoss();
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      if (isAuthenticated()) {
        await refreshSession();
      }
      if (!cancelled) setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshSession]);

  return (
    <AuthContext.Provider value={{ user, ready, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
